<template>
  <v-content class="mt-0 pt-0">
    <v-container fluid>
      <pr-loading v-model="loading" />
      <alert ref="msg" />

      <v-toolbar flat color="white" class="elevation-1">
        <v-toolbar-title>{{ $t('Claims') }}</v-toolbar-title>

        <v-spacer></v-spacer>

        <v-text-field :label="$t('Manifest')" v-model="manifest" hide-details
          append-icon="search" @click:append="search()"
          clearable @click:clear="search(true)"
          @keyup.enter="search()"
          class="mr-3"
          style="max-width: 19rem"
        ></v-text-field>

        <v-input-date-range-picker
          v-model="dates"
          @input="loadCases"
          :max="tomorrowDate"
          :label="$t('For period')"
          standard-style
          class="mr-2"
        />

        <v-tooltip bottom>
          <v-btn slot="activator" flat color="primary" :href="openClaimform()" target="_blank">
            <v-icon left>add_link</v-icon>
            <span>{{ $t('New claim') }}</span>
          </v-btn>
          <span>{{ $t('New claim') }}</span>
        </v-tooltip>

        <v-divider class="mx-2" inset vertical></v-divider>

        <table-column-settings :headers="headers"></table-column-settings>
      </v-toolbar>

      <v-layout row wrap>
        <v-flex xs12>
          <v-data-table
            :headers="headersFiltered"
            :items="items"
            itemid="id"
            :pagination.sync="pagination"
            :total-items="totalItems"
            :loading="loading"
            :rows-per-page-items="[5, 10, 25]"
            :rows-per-page-text="$t('Rows per page')"
            class="elevation-1"
          >
            <template v-slot:items="{ item }">
              <td v-for="(header, index) in headersFiltered" :key="index"
                :class="['text-xs-'+header.align, header.class]"
                class="px-3"
              >

                <template v-if="header.value === 'date'">{{ printDateTime(item.date) }}</template>

                <template v-else-if="header.value === 'manifest'">{{ item.manifest }}</template>

                <template v-else-if="header.value === 'type'"><v-chip v-if="item.type && (item.type === 'PROBLEM' || item.type === 'CLARIFICATION')"
                    :color="getCollectionItemById(catalog.caseTypes, item.type).color"
                    text-color="white"
                  >
                    <v-icon class="mr-2">{{ getCollectionItemById(catalog.caseTypes, item.type).icon }}</v-icon>
                    {{ ($t('CASE_TYPE.' + item.type)).toUpperCase() }}
                  </v-chip></template>

                <template v-else-if="header.value === 'subtype'"><v-chip v-if="item.subtype"
                    :color="getCollectionItemById(catalog.caseSubtypes, item.subtype).color"
                    :text-color="getCollectionItemById(catalog.caseSubtypes, item.subtype).color"
                    outline
                  >{{ $t('CASE_SUBTYPE.' + item.subtype).toUpperCase() }}</v-chip></template>

                <template v-else-if="header.value === 'title'">{{ item.title }}</template>

                <template v-else-if="header.value === 'state'"><v-chip label v-if="item.state"
                    :color="getCollectionItemById(catalog.caseStatuses, item.state).color"
                    text-color="white"
                  >
                    <v-icon left>{{ getCollectionItemById(catalog.caseStatuses, item.state).icon }}</v-icon>
                    {{ $t('CASE_STATUS.' + item.state).toUpperCase() }}
                  </v-chip></template>

                <template v-else-if="header.value === 'comment'">{{ item.comment }}</template>
                <template v-else-if="header.value === 'comment_date'">{{ printDateTime(item.comment_date) }}</template>

                <template v-else-if="header.value === 'value'">{{ showAmount(item.value) }}</template>

                <template v-else-if="header.value === 'attachment'"><v-tooltip bottom v-if="item.shipment">
                    <attachments slot="activator" :key="item.shipment.id" :shipment="item.shipment" flag="ins" non-deleteable />
                    {{ $t('Attachments') }}
                  </v-tooltip></template>

                <template v-else>
                  <span>{{ item[header.value] }}</span>
                </template>
              </td>

            </template>
          </v-data-table>
          <div class="text-xs-center pt-2">
            <v-pagination v-model="pagination.page" :length="pages" circle total-visible="7"></v-pagination>
          </div>
        </v-flex>
      </v-layout>
    </v-container>
  </v-content>
</template>


<script>
import PrLoading             from "@/components/Loading";
import Alert                 from '@/components/Alert.vue';
import Attachments           from '@/components/Attachments.vue';
import TableColumnSettings   from '@/components/TableColumnSettings.vue';
import VInputDateRangePicker from '@/components/VInputDateRangePicker.vue';
import { extractBodyErrorMessages, printDateTime, showAmount } from "@/components/util";
import { cases }             from "@/resources";
import { mapGetters, mapActions } from "vuex";

const catalog = {
  caseStatuses: [
    { id: "OPEN",          color: "blue-grey lighten-3", icon: "radio_button_unchecked", isDefault: true },
    { id: "WORK",          color: "blue lighten-2",      icon: "schedule"                },
    { id: "DONE",          color: "green lighten-2",     icon: "check_circle"            },
    { id: "CLARIFICATION", color: "blue lighten-1",      icon: "info"                    },
  ],
  caseTypes: [
    { id: "PROBLEM",       subtypeField: 'problem_type',       color: "orange",         icon: "warning" },
    { id: "CLARIFICATION", subtypeField: 'clarification_type', color: "blue lighten-1", icon: "message" },
    { id: "COMMENDATION",  subtypeField: null,                 color: "tp_green",       icon: "try"     },
  ],
  caseSubtypes: [
    { id: "DEVIATION",  type: "PROBLEM",       color: "orange lighten-3",     },
    { id: "MISSING",    type: "PROBLEM",       color: "orange lighten-1",     },
    { id: "DAMAGE",     type: "PROBLEM",       color: "orange darken-1",      },
    { id: "OTHER",      type: "PROBLEM",       color: "blue-grey lighten-3", isDefault: true },
    { id: "CHECK",      type: "CLARIFICATION", color: "blue lighten-3",       },
    { id: "CORRECTION", type: "CLARIFICATION", color: "blue lighten-1",       },
    { id: "OTHER",      type: "CLARIFICATION", color: "blue-grey lighten-3", isDefault: true },
  ]
}

export default {
  components: {
    PrLoading,
    Alert,
    Attachments,
    TableColumnSettings,
    VInputDateRangePicker
  },
  data() {
    return {

      loading: false,
      totalItems: 0,
      items: [],
      pagination: {
        totalItems: 1,
        sortBy: 'date',
        descending: true
      },

      headers: [
        { text: this.$t('Date'),              value: 'date',         align: 'left',   sortable: true,  show: true, class: 'text-no-wrap caption line-height-120' },
        { text: this.$t('Manifest'),          value: 'manifest',     align: 'left',   sortable: false, show: true },
        { text: this.$t('Type'),              value: 'type',         align: 'right',  sortable: false, show: true },
        { text: this.$t('Subtype'),           value: 'subtype',      align: 'left',   sortable: false, show: true },
        { text: this.$t('Title'),             value: 'title',        align: 'left',   sortable: false, show: true, class: 'caption font-weight-bold line-height-120' },
        { text: this.$t('State'),             value: 'state',        align: 'left',   sortable: false, show: true },
        { text: this.$t('Approved value'),    value: 'value',        align: 'right',  sortable: false, show: true },
        { text: this.$t('Documents'),         value: 'attachment',   align: 'center', sortable: false, show: true },
      ],

      manifest: '',
      dates: {
        start_date: null,
        end_date: null
      },

      catalog,
      printDateTime,
      showAmount
    }
  },

  computed: {
    ...mapGetters(["userInfo"]),
    pages() {
      if (this.pagination.rowsPerPage == null ||
        this.totalItems == null
      ) return 0;
      return Math.ceil(this.totalItems / this.pagination.rowsPerPage);
    },
    headersFiltered() {
      return this.headers.filter(elem => elem.show);
    },
    tomorrowDate() {
      let tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);// +1 day
      return tomorrow.toISOString().slice(0, 10);
    }
  },

  watch: {
    pagination: {
      handler() {
        this.loadCases();
      },
      deep: true
    }
  },

  methods: {

    loadCases() {
      if (this.loading) {
        return;
      }
      this.loading = true;

      let params = this.paginationParams();
      if (this.manifest) {
        params.manifest = this.manifest;
      }
      if (this.dates.start_date) {
        params.after = new Date(this.dates.start_date).getTime();
        params.until = new Date(this.dates.end_date || this.tomorrowDate).getTime();
      }

      cases.list( params ).then(res => {

        this.initializeCases(res.body.result);
        this.totalItems = res.body.length;

      }).catch(err => {
        console.log('cases error:', err);
        this.$refs.msg.popup( extractBodyErrorMessages(err.body), 'error');
      }).finally(() => {
        this.loading = false
      });
    },

    initializeCases(resBodyData) {
      let items = resBodyData;
      items.forEach(item => {

        item.subtype = item[ (catalog.caseTypes.find(elem => elem.id === item.type) || {}).subtypeField ] || null;

        // Shipments
        item.shipment = {
          id: item.shipment_id,
          attachment_count: 0
        }
      });
      this.items = items;
    },

    paginationParams() {
      const { sortBy, descending, page, rowsPerPage } = this.pagination;

      let offset = (page>=1 && rowsPerPage>=1) ? (page - 1) * rowsPerPage : 0;
      let length = (rowsPerPage>=1) ? rowsPerPage : -1;
      let sort   = (sortBy) ? (descending ? "-" : "") + sortBy : "";

      return { offset, length, sort };
    },

    getCollectionItemById(collection, id, fieldname) {
      let prop = {};
      const elems = Array.isArray(collection) ? collection : this[collection];
      if (!fieldname)
        fieldname = 'id';
      if (elems && id) {
        prop = elems.find(elem => elem[fieldname] == id) || {};
      }
      return prop;
    },

    search(isClear) {
      if (isClear) {
        this.manifest = '';
        this.loadCases();
      }
      else if (this.manifest.length > 1) {
        this.loadCases();
      }
    },

    openClaimform() {
      return 'https://app.transpress.bg/claimform/#/claim-form/'
    }
  },

  mounted() {
    this.loadCases();
  },
}
</script>

<style scoped>
.line-height-100 {
  line-height: 1.0 !important;
}
.line-height-110 {
  line-height: 1.1 !important;
}
.line-height-120 {
  line-height: 1.2 !important;
}

/* :deep() .v-datatable thead th.column {
  white-space: normal;
} */
</style>